import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { Spin } from 'antd';

// Stage 5N-9A — shared in-app HTML report viewer used by both the
// tenant invoice viewer and the internal-admin invoice viewer.
//
// The iframe is sandboxed (`allow-same-origin allow-modals` only — no
// `allow-scripts`) so the report HTML can never execute JavaScript in
// the SPA's origin. Same-origin is required so the SPA can:
//   1) drive zoom by writing the `--gi-zoom` CSS variable on the
//      iframe's <html> element, and
//   2) measure `iframe.contentDocument.documentElement.scrollHeight`
//      to size the iframe to its actual content (no fixed 80vh, no
//      bottom-of-A4 clipping, no second/inner scrollbar at 100% zoom).
//
// Zoom uses the CSS `zoom` property — well supported in Chromium /
// WebKit / modern Firefox (Firefox 126+) and the only scaling option
// that updates layout (so `scrollHeight` reflects the visual height
// after scaling, which is exactly what we want for iframe sizing).
//
// Print: `@media print { .gi-viewer { zoom: 1 !important; } }` in
// the Java wrapper resets to 100% so prints always go out at true A4
// regardless of the on-screen zoom level. The browser's "Headers and
// footers" toggle is outside our control — we surface a small viewer-
// only hint above the iframe (it lives in the SPA chrome and never
// appears inside the printable iframe document).
const PRINT_HINT =
  'Tip: for a clean invoice copy, turn off "Headers and footers" in your browser\'s print settings.';

const ReportViewer = forwardRef(function ReportViewer(
  {
    html,
    loading,
    zoom = 1,
    // Optional labels so non-invoice callers (e.g. the GRN receipt
    // viewer) read cleanly. Defaults preserve the original invoice
    // behaviour exactly.
    title = 'Subscription invoice report',
    loadingTip = 'Loading invoice report…'
  },
  ref
) {
  const iframeRef = useRef(null);
  // Initial render falls back to a comfortable loader-sized box; the
  // first measurement after iframe load replaces this with the true
  // document height.
  const [docHeight, setDocHeight] = useState(720);

  const applyZoom = useCallback((value) => {
    const f = iframeRef.current;
    if (!f) return;
    const doc = f.contentDocument;
    if (!doc || !doc.documentElement) return;
    doc.documentElement.style.setProperty('--gi-zoom', String(value));
  }, []);

  const measureHeight = useCallback(() => {
    const f = iframeRef.current;
    if (!f) return;
    const doc = f.contentDocument;
    if (!doc || !doc.documentElement) return;
    // Because we use CSS `zoom` (not `transform: scale`), scrollHeight
    // already reflects the post-zoom layout — no manual multiplication.
    // The +24 buffer absorbs sub-pixel rounding so the last paint row
    // never gets shaved by a 1px overflow scrollbar.
    const measured = doc.documentElement.scrollHeight;
    if (measured > 0) setDocHeight(measured + 24);
  }, []);

  // Apply zoom + remeasure on every zoom or html change. The zoom
  // variable is set synchronously, but the browser may need a paint
  // before scrollHeight reflects the new layout — rAF gives us that.
  useEffect(() => {
    applyZoom(zoom);
    const id = requestAnimationFrame(() => measureHeight());
    return () => cancelAnimationFrame(id);
  }, [zoom, html, applyZoom, measureHeight]);

  // Recalculate on window resize so a viewport change (browser zoom,
  // dock/undock devtools, sidebar collapse) keeps the iframe height
  // honest. ResizeObserver on the iframe document catches anything
  // that reflows inside the iframe (font swap, image decode, etc).
  useEffect(() => {
    const onResize = () => measureHeight();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [measureHeight]);

  const handleLoad = useCallback(() => {
    applyZoom(zoom);
    measureHeight();
    // Watch internal reflows of the iframe content (e.g. font load
    // after the initial paint). Also catches the rare case where
    // scrollHeight is briefly 0 right after srcDoc swap.
    const f = iframeRef.current;
    const doc = f && f.contentDocument;
    if (!doc || !doc.documentElement) return;
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measureHeight());
    ro.observe(doc.documentElement);
    // Stash the observer on the iframe so the next load can disconnect.
    if (f.__giObserver) {
      try { f.__giObserver.disconnect(); } catch (_e) { /* noop */ }
    }
    f.__giObserver = ro;
  }, [zoom, applyZoom, measureHeight]);

  useEffect(() => {
    return () => {
      const f = iframeRef.current;
      if (f && f.__giObserver) {
        try { f.__giObserver.disconnect(); } catch (_e) { /* noop */ }
        f.__giObserver = null;
      }
    };
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      print: () => {
        const w = iframeRef.current && iframeRef.current.contentWindow;
        if (!w) return;
        try {
          w.focus();
          w.print();
        } catch (_e) {
          /* sandbox forbids print — surfaced via the disabled toolbar button */
        }
      }
    }),
    []
  );

  return (
    <div>
      {!loading ? (
        <div className="text-xs text-ink-400 mb-2">{PRINT_HINT}</div>
      ) : null}
      <div className="rounded-xl bg-surface-100 border border-surface-200 overflow-hidden">
        {loading ? (
          <div
            className="flex items-center justify-center"
            style={{ minHeight: 720 }}
          >
            <Spin tip={loadingTip} />
          </div>
        ) : (
          <iframe
            ref={iframeRef}
            title={title}
            sandbox="allow-same-origin allow-modals"
            srcDoc={html || ''}
            onLoad={handleLoad}
            scrolling="no"
            style={{
              width: '100%',
              height: docHeight,
              border: 'none',
              background: '#e9eef4',
              display: 'block'
            }}
          />
        )}
      </div>
    </div>
  );
});

export default ReportViewer;
